import { cn } from '@/lib/utils'

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton', className)} />
}

export function CardSkeleton() {
  return (
    <div className="rounded-xl bg-card border border-border p-4 space-y-3">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
      <Skeleton className="h-3 w-full" />
    </div>
  )
}

export function PageSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CardSkeleton />
        <CardSkeleton />
      </div>
    </div>
  )
}

/** Skeleton for watch pages (ProgramView / LessonView) */
export function WatchPageSkeleton() {
  return (
    <div className="flex gap-6 max-w-[1400px] mx-auto">
      {/* Main content */}
      <div className="flex-1 min-w-0 space-y-4">
        {/* Video player */}
        <Skeleton className="w-full aspect-video rounded-lg" />

        {/* Title + bookmark */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-7 w-3/4" />
          </div>
          <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
        </div>

        {/* Info bar + mark complete */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-9 w-44 rounded-lg" />
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-4">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-5 w-36" />
        </div>

        {/* Content */}
        <div className="space-y-2 pt-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
          <Skeleton className="h-3 w-2/3" />
        </div>

        {/* Instructor */}
        <div className="rounded-xl bg-card border border-border p-4">
          <Skeleton className="h-4 w-16 mb-3" />
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-full shrink-0" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="hidden xl:block w-80 shrink-0">
        <div className="rounded-xl bg-card border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <Skeleton className="h-4 w-40" />
          </div>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="border-b border-border last:border-0 px-4 py-3 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <div className="pl-6 space-y-1.5">
                {Array.from({ length: 3 }).map((_, j) => (
                  <Skeleton key={j} className="h-3 w-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Skeleton for catalog pages (carousels) */
export function CatalogSkeleton() {
  return (
    <div className="space-y-8 max-w-[1600px] mx-auto">
      <Skeleton className="h-8 w-48" />
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="space-y-3">
          <Skeleton className="h-5 w-36" />
          <div className="flex gap-2">
            {Array.from({ length: 6 }).map((_, j) => (
              <div key={j} className="shrink-0 w-[170px] space-y-2">
                <Skeleton className="aspect-[2/3] rounded" />
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-2.5 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
